import React from 'react';
import { cn } from '@/lib/utils';
import * as LucideIcons from 'lucide-react';
import exp from 'constants';

// Default stroke properties
const defaultStroke = {
  width: 2.5,
  linecap: 'round' as const,
  linejoin: 'round' as const,
};

// Helper type for icon props
type IconProps = {
  className?: string;
  size?: number | string;
  color?: string;
  strokeWidth?: number;
} & React.SVGProps<SVGSVGElement>;

// Updated function to create an icon component
const createIconComponent = (Icon: LucideIcons.LucideIcon) => {
  // eslint-disable-next-line react/display-name
  return ({
            className,
            size = 24,
            color,
            strokeWidth = defaultStroke.width,
            fill = 'none',
            ...props
          }: IconProps) => (
    <Icon
      className={cn(color, className)}
      size={size}
      strokeWidth={strokeWidth}
      fill={fill}
      {...props}
    />
  );
};


// Create icon new-chatbot-components
export const IconAlertCircle = createIconComponent(LucideIcons.AlertCircle);
export const IconArrowRight = createIconComponent(LucideIcons.ArrowRight);
export const IconArrowDown = createIconComponent(LucideIcons.ArrowDown);
export const IconCheck = createIconComponent(LucideIcons.Check);
export const IconChevronDown = createIconComponent(LucideIcons.ChevronDown);
export const IconChevronLeft = createIconComponent(LucideIcons.ChevronLeft);
export const IconChevronRight = createIconComponent(LucideIcons.ChevronRight);
export const IconChevronUp = createIconComponent(LucideIcons.ChevronUp);
export const IconChevronsUpDown = createIconComponent(LucideIcons.ChevronsUpDown);
export const IconCircle = createIconComponent(LucideIcons.Circle);
export const IconCopy = createIconComponent(LucideIcons.Copy);
export const IconEdit = createIconComponent(LucideIcons.Edit);
export const IconFile = createIconComponent(LucideIcons.File);
export const IconFileText = createIconComponent(LucideIcons.FileType);
export const IconHelpCircle = createIconComponent(LucideIcons.HelpCircle);
export const IconImage = createIconComponent(LucideIcons.Image);
export const IconLoader2 = createIconComponent(LucideIcons.Loader2);
export const IconMoon = createIconComponent(LucideIcons.Moon);
export const IconMoreHorizontal = createIconComponent(LucideIcons.MoreHorizontal);
export const IconPlusCircle = createIconComponent(LucideIcons.PlusCircle);
export const IconSettings = createIconComponent(LucideIcons.Settings);
export const IconSun = createIconComponent(LucideIcons.Sun);
export const IconUser = createIconComponent(LucideIcons.UserRound);
export const IconX = createIconComponent(LucideIcons.X);
export const IconGlobe = createIconComponent(LucideIcons.Globe);
export const IconLockClosed = createIconComponent(LucideIcons.Lock);
export const IconHeart = createIconComponent(LucideIcons.Heart);
export const IconShare = createIconComponent(LucideIcons.Share2);
export const IconEye = createIconComponent(LucideIcons.Eye);
export const IconMessage = createIconComponent(LucideIcons.MessageCircle);
export const IconVerticalDots = createIconComponent(LucideIcons.EllipsisVertical);
export const IconClock = createIconComponent(LucideIcons.Clock);
export const IconShare2 = createIconComponent(LucideIcons.Share);
export const IconList = createIconComponent(LucideIcons.List);
export const IconInfo = createIconComponent(LucideIcons.Info);
export const IconBell = createIconComponent(LucideIcons.Bell);
export const IconSearch = createIconComponent(LucideIcons.Search);
export const IconHorizontalDots = createIconComponent(LucideIcons.Ellipsis);
export const IconArrowUp = createIconComponent(LucideIcons.ArrowUp);
export const IconSettings2 = createIconComponent(LucideIcons.Settings2);
export const IconText = createIconComponent(LucideIcons.Type);
export const IconExit = createIconComponent(LucideIcons.LogOut);
export const IconPlus = createIconComponent(LucideIcons.Plus);
export const IconTrash = createIconComponent(LucideIcons.Trash);
export const IconPencil = createIconComponent(LucideIcons.Pencil);
export const IconLink = createIconComponent(LucideIcons.Link);
export const IconDotFilled = createIconComponent(LucideIcons.Dot);
export const IconWand = createIconComponent(LucideIcons.WandSparkles);
export const IconLinkedIn = createIconComponent(LucideIcons.Linkedin);
export const IconPaperPlane = createIconComponent(LucideIcons.Send);
export const IconEraser = createIconComponent(LucideIcons.Eraser);
export const IconZoomIn = createIconComponent(LucideIcons.ZoomIn);
export const IconDownload = createIconComponent(LucideIcons.Download);
export const IconArrowCornerDownLeft = createIconComponent(LucideIcons.CornerDownLeft);
export const IconWifiOff = createIconComponent(LucideIcons.WifiOff);
export const IconWifiOn = createIconComponent(LucideIcons.Wifi);
export const IconPenSquare = createIconComponent(LucideIcons.PenSquare);
export const IconMap = createIconComponent(LucideIcons.MapPinned);
export const IconEmail = createIconComponent(LucideIcons.Mail);
export const IconExternalLink = createIconComponent(LucideIcons.ExternalLink);
export const IconMessageSquareDot = createIconComponent(LucideIcons.MessageSquareDot);
export const IconMessageSquare = createIconComponent(LucideIcons.MessageSquare);
export const IconPinLucide = createIconComponent(LucideIcons.Pin);
export const IconPinOff = createIconComponent(LucideIcons.PinOff);
export const IconShield = createIconComponent(LucideIcons.Shield);
export const IconShieldCheck = createIconComponent(LucideIcons.ShieldCheck);
export const IconShieldPending = createIconComponent(LucideIcons.ShieldQuestion);
export const IconStar = createIconComponent(LucideIcons.Star);
export const IconAttachment = createIconComponent(LucideIcons.Paperclip);
export const IconUsers = createIconComponent(LucideIcons.Users);
export const IconLock = createIconComponent(LucideIcons.Lock);
export const IconCamera = createIconComponent(LucideIcons.Camera);
export const IconPictureInPicture2 = createIconComponent(LucideIcons.PictureInPicture2);
export const IconEmoji = createIconComponent(LucideIcons.Smile);
export const Bot = createIconComponent(LucideIcons.Bot);
export const Users = createIconComponent(LucideIcons.Users);
export const GemIcon = createIconComponent(LucideIcons.Gem);
export const IconAlert = createIconComponent(LucideIcons.AlertCircle);
export const IconChat = createIconComponent(LucideIcons.MessageCircleMore);
export const IconShieldEllipsis = createIconComponent(LucideIcons.ShieldEllipsis);
export const IconUserRoundPen = createIconComponent(LucideIcons.UserRoundPen);
export const IconBuilding = createIconComponent(LucideIcons.Building);
export const IconUserRound = createIconComponent(LucideIcons.UserRound);
export const IconEnvelope = createIconComponent(LucideIcons.Mail);
export const IconUsers2 = createIconComponent(LucideIcons.Users2);
export const IconMessageCircle = createIconComponent(LucideIcons.MessageCircle);
export const IconUserGroup = createIconComponent(LucideIcons.Group);
export const IconLogIn = createIconComponent(LucideIcons.LogIn);
export const IconHandshake = createIconComponent(LucideIcons.Handshake);
export const IconSwipe = createIconComponent(LucideIcons.Hand);
export const IconFlag = createIconComponent(LucideIcons.Flag);
export const IconInbox = createIconComponent(LucideIcons.Inbox);
export const IconUserPlus = createIconComponent(LucideIcons.UserRoundPlus);
export const IconRefresh = createIconComponent(LucideIcons.RefreshCcw);
export const IconLockedCommunity = createIconComponent(LucideIcons.Lock);
export const IconTrendingUp = createIconComponent(LucideIcons.TrendingUp);
export const IconCompass = createIconComponent(LucideIcons.Compass);
export const IconTrophy = createIconComponent(LucideIcons.Trophy);
export const IconBug = createIconComponent(LucideIcons.Bug);
export const IconBriefcase = createIconComponent(LucideIcons.Briefcase);
export const IconGraduationCap = createIconComponent(LucideIcons.GraduationCap);
export const IconArrowLeft = createIconComponent(LucideIcons.ArrowLeft);
export const IconMapPin = createIconComponent(LucideIcons.MapPin);
export const IconTime = createIconComponent(LucideIcons.Clock);
export const IconPremium = createIconComponent(LucideIcons.Star);
export const IconChartBar = createIconComponent(LucideIcons.BarChart);
export const IconSparkles = createIconComponent(LucideIcons.Sparkles);
export const IconBookmark = createIconComponent(LucideIcons.Bookmark);
export const IconRocket = createIconComponent(LucideIcons.Rocket);
export const IconSmartphone = createIconComponent(LucideIcons.Smartphone);
export const IconBookOpenCheck = createIconComponent(LucideIcons.BookOpenCheck);
export const IconFileUp = createIconComponent(LucideIcons.FileUp);
export const IconGrid = createIconComponent(LucideIcons.Grid);
export const IconCalendar1 = createIconComponent(LucideIcons.CalendarDays);
export const IconFileSpreadsheet = createIconComponent(LucideIcons.FileType);
export const IconFileCloud = createIconComponent(LucideIcons.FileUp);
export const IconCloud = createIconComponent(LucideIcons.Cloud);
export const IconMicrosoft = createIconComponent(LucideIcons.Box);
export const IconBrain = createIconComponent(LucideIcons.Brain);

// Import the Google Docs and Sheets icons from public folder
// Custom icon creator for static SVG files
const createStaticIconComponent = (path: string) => {
  // eslint-disable-next-line react/display-name
  return ({ 
    className, 
    size = 24, 
    ...props 
  }: {
    className?: string;
    size?: number | string;
    [key: string]: any;
  }) => (
    <img 
      src={path} 
      alt="" 
      className={cn("", className)} 
      width={size} 
      height={size} 
      {...props} 
    />
  );
};

// Create icon components for Google Docs and Sheets
export const GoogleDocsIcon = createStaticIconComponent('/logos/google-docs-logo.png');
export const GoogleSheetsIcon = createStaticIconComponent('/logos/google-sheets-logo.png');

// Add more icons as needed
